import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClientV2 } from '~/api/client';
import clientApi, {
  IClientApiResponse,
  mapCreatorsFromApiModelToViewModel,
} from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import {
  SAVED_LIST_CREATORS,
  SAVED_LIST_CREATORS_SEARCH,
} from '~/queries/keys';

const SHARED_SAVED_LIST_CREATORS_PAGE_SIZE = 10;

interface Params {
  creatorIds?: string[];
  id: string;
  pageSize?: number;
  properties?: string[];
  sortDirection?: string;
  sortField?: string;
  enabled?: boolean;
}

export const useGetSavedListCreators = ({
  creatorIds,
  id,
  pageSize = SHARED_SAVED_LIST_CREATORS_PAGE_SIZE,
  properties,
  sortField,
  sortDirection,
  enabled = true,
}: Params) => {
  const queryKey: any[] = [
    SAVED_LIST_CREATORS,
    id,
    { creatorIds, pageSize, sortField, sortDirection, properties },
  ];

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      clientApi.savedLists.creators({
        'sort[]': `${sortField}:${sortDirection}`,
        creatorIds,
        id,
        page: pageParam,
        pageSize,
        properties,
      }),
    enabled: !!id && enabled,
    staleTime: 1000 * 60 * 30, //30 mins
    retry: 0,
    getNextPageParam: (page, pages) =>
      Math.floor(pages.length * pageSize) <= (page?.totalItems ?? 0)
        ? pages.length + 1
        : undefined,
    keepPreviousData: true,
  });

  const mappedData = useMemo(() => {
    const data = query.data;

    if (!data) {
      return {
        creators: [],
        totalItems: 0,
      };
    }

    const pages = data.pages?.reduce(
      (acc: IClientApiResponse<Creator>, page) => {
        acc.results = [...acc.results, ...page.results];
        acc.totalItems = page.totalItems;

        return acc;
      },
      {
        results: [],
        totalItems: 0,
      },
    );

    return {
      creators: mapCreatorsFromApiModelToViewModel(pages.results),
      totalItems: pages.totalItems,
    };
  }, [query]);

  return { ...query, data: mappedData, queryKey, originalQuery: query };
};

interface UseSavedListCreatorsV2Params {
  creatorIds?: string[];
  enabled?: boolean;
  id: string;
}

type SavedListCreator = {
  creatorId: string;
  notes: string;
};

export const useGetSavedListCreatorsSearch = ({
  creatorIds,
  enabled = true,
  id,
}: UseSavedListCreatorsV2Params) => {
  const queryKey = [SAVED_LIST_CREATORS_SEARCH, id, { creatorIds }];

  const { data, ...query } = useQuery({
    queryKey,
    queryFn: () =>
      apiClientV2.post(
        `/saved-lists/${id}/creators/notes/search`,
        {
          creatorIds,
        },
        {
          headers: {
            'content-type': 'application/json',
          },
        },
      ),
    select: ({ data }) => {
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
    enabled: !!id && enabled && !!creatorIds?.length,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  return {
    ...query,
    data: data as SavedListCreator[],
    queryKey,
  };
};
